// AuthContext.js
import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { jwtDecode } from "jwt-decode";  // Ensure you installed via: npm install jwt-decode
import axios from "axios";
import { View, Text, TouchableOpacity, StyleSheet } from "react-native";
import { useTranslation } from "./i18n/useTranslation";
import { isSubscriptionExpired402 } from "./utils/subscriptionExpired";

import { FASTAPI_BASE_URL } from "./config";  // import from your config file
const REFRESH_TOKEN_URL = `${FASTAPI_BASE_URL}/refresh-token`;
axios.defaults.baseURL = FASTAPI_BASE_URL;
// BDD 9/7/2025
// const ENABLE_ENSURE = true;      // flip to run the test
// BDD 10/3/2025 - do not want this
const ENABLE_ENSURE = false;      // turn it off

let ENSURE_PROMISE = null;
const lastRotationAtRef = { current: 0 };

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const CLIENT_SESSION_ID_KEY = "client_session_id";
const generateClientSessionId = () => {
  // dependency-free unique-ish id
  return `cs_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
};

// Collapse empty / sentinel language values ("", "none", "null", "auto") to a
// concrete default so a backend sentinel never paints as the literal "NONE" on
// the UI language chip. Real codes (en, es, gu, …) pass through unchanged.
const SECONDARY_LANG_SENTINELS = new Set(["", "none", "null", "undefined", "auto"]);
const normalizeSecondaryLang = (lang) => {
  const code = String(lang ?? "").trim().toLowerCase();
  return SECONDARY_LANG_SENTINELS.has(code) ? "en" : code;
};

export const AuthProvider = ({ children }) => {
    const [authToken, setAuthToken] = useState(null);
    const [user, setUser] = useState(null); // Full student profile
    const [isLoading, setIsLoading] = useState(true);
    const [selectedSecondaryLang, setSelectedSecondaryLang] = useState("en");
    const [contentDb, setContentDb] = useState("anatomy"); // default
    const [authExpired, setAuthExpired] = useState(false); // BDD 10/3/2025
    const isLoggingOutRef = useRef(false);

    // WP7 (AIT-291): the backend answered 402 subscription_expired, so flag the
    // stored user restricted (immutable update) and let the navigator gate.
    const markSubscriptionRestricted = async () => {
      setUser((prev) =>
        !prev || prev.subscription_restricted === true ? prev : { ...prev, subscription_restricted: true }
      );
      try {
        const raw = await AsyncStorage.getItem("student_data");
        const existing = raw ? JSON.parse(raw) : {};
        await AsyncStorage.setItem(
          "student_data",
          JSON.stringify({ ...existing, subscription_restricted: true })
        );
      } catch (err) {
        console.warn("[AuthContext] could not persist subscription_restricted:", err);
      }
    };

    // Central place for axios calls (most gated endpoints go through axios).
    useEffect(() => {
      const responseInterceptors = axios?.interceptors?.response;
      if (!responseInterceptors?.use) return undefined;
      const id = responseInterceptors.use(
        (response) => response,
        (error) => {
          if (isSubscriptionExpired402(error?.response?.status, error?.response?.data)) {
            markSubscriptionRestricted();
          }
          return Promise.reject(error);
        }
      );
      return () => {
        if (responseInterceptors.eject) responseInterceptors.eject(id);
      };
    }, []);

    // fetch() path: peek at a 402 body without consuming it.
    const flagIfSubscriptionExpired = async (res) => {
      if (res?.status !== 402) return;
      try {
        const data = await res.clone().json();
        if (isSubscriptionExpired402(res.status, data)) await markSubscriptionRestricted();
      } catch (_) {
        // body is not JSON - not our 402
      }
    };

    // BDD 9/7/2025
    const focusTimerRef = useRef(null);
      useEffect(() => {
        if (!ENABLE_ENSURE) return;

        const onVisible = () => {
          const rid = Math.floor(Math.random()*10000);
          console.warn(`[ensure@focus][${rid}] visible (debounce start)`);
          if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
          focusTimerRef.current = setTimeout(async () => {
            console.warn(`[ensure@focus][${rid}] debounce fired`);
            const result = await ensureSession(rid, "focus");
            console.warn(`[ensure@focus][${rid}] result`, result);
            focusTimerRef.current = null;
          }, 600);
        };

        const onVis = () => { if (document.visibilityState === "visible") onVisible(); };
        document.addEventListener("visibilitychange", onVis);
        return () => {
          if (focusTimerRef.current) { clearTimeout(focusTimerRef.current); focusTimerRef.current = null; }
          document.removeEventListener("visibilitychange", onVis);
        };
      }, []);

    useEffect(() => {
        const loadAuthData = async () => {
          try {
            // Pull whatever you stored previously
            const token = await AsyncStorage.getItem("auth_token");
            const userJson = await AsyncStorage.getItem("student_data");
            console.log("[AuthContext]  stored token:", token);
            console.log("[AuthContext]  stored userJson:", userJson);

            if (token && userJson) {
              console.log("[AuthContext] restoring session via login()");
              const studentData = JSON.parse(userJson);
              await login(token, studentData);
            }

            // load the saved UI language
            const savedLang = await AsyncStorage.getItem("secondary_language_id");
            setSelectedSecondaryLang(normalizeSecondaryLang(savedLang));
            
          } catch (err) {
            console.error("Error bootstrapping auth:", err);
          } finally {
            console.log("[AuthContext] ✅ loadAuthData done");
            // ← clear the loading flag no matter what
            setIsLoading(false);
          }
        };
        loadAuthData();
      }, []);

      // inside AuthContext.js
    // BDD 10/3/2025
    let refreshPromise = null;

    // BDD 11/6/2025 - try 4
    const refreshAuthToken = async () => {
      if (refreshPromise) return refreshPromise;

      refreshPromise = (async () => {
        try {
          const [storedRefresh, storedAccess] = await Promise.all([
            AsyncStorage.getItem("refresh_token"),
            AsyncStorage.getItem("auth_token"),
          ]);

          const tokenToUse = storedRefresh || storedAccess;

          if (!tokenToUse) {
            console.warn("refreshAuthToken: no token available to refresh");
            return null; // caller will trigger sign-in flow
          }

          const res = await fetch(`${FASTAPI_BASE_URL}/refresh-token`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: tokenToUse.startsWith("Bearer ")
                ? tokenToUse
                : `Bearer ${tokenToUse}`,
            },
            body: JSON.stringify({}), // backend ignores body
          });

          if (!res.ok) {
            console.warn("refreshAuthToken: server returned", res.status);
            return null;
          }

          const json = await res.json();

          const newAccess =
            json?.access_token ??
            json?.token ??
            json?.data?.access_token;

          const newRefresh =
            json?.refresh_token ??
            json?.data?.refresh_token ??
            newAccess; // ok if same for AI Tutor

          if (!newAccess) {
            console.warn("refreshAuthToken: no access token in response");
            return null;
          }

          await AsyncStorage.multiSet([
            ["auth_token", newAccess],
            ["refresh_token", newRefresh],
          ]);

          setAuthExpired(false);
          setAuthToken(newAccess);
          axios.defaults.headers.common["Authorization"] = newAccess;

          return newAccess;
        } catch (e) {
          console.warn("refreshAuthToken error:", e?.message || e);
          return null;
        } finally {
          refreshPromise = null;
        }
      })();

      return refreshPromise;
    };

// AuthContext.js
const fetchWithAuth = async (url, options = {}) => {
  const baseHeaders = options.headers || {};
  const rawAuth = options.rawAuth === true; // <-- pass this per-call when an endpoint expects raw token

  const makeHeaders = (t) => ({
    ...options,
    headers: {
      ...baseHeaders,
      Authorization: t ? (rawAuth ? t : `Bearer ${t}`) : undefined,
    },
  });

  let token = authToken || (await AsyncStorage.getItem("auth_token"));

  // first attempt
  let res = await fetch(url, makeHeaders(token));
  await flagIfSubscriptionExpired(res);
  if (res.status !== 401) return res;

  // If we're logged out or logging out, do not show session-expired UI
  if (!token || isLoggingOutRef.current) return res;

  // refresh once, then retry
  const newToken = await refreshAuthToken();
  if (!newToken) {
    if (!isLoggingOutRef.current) {
      setAuthExpired(true); // show banner if we truly can't recover
    }
    return res;
  }
  const retried = await fetch(url, makeHeaders(newToken));
  await flagIfSubscriptionExpired(retried);
  return retried;
};


    // BDD 10/3/2025
    const login = async (token, studentData) => {
      console.log("[AuthContext] ▶️ login() called with token, studentData:", token, studentData);

      // clear any expired-session banner from previous run
      setAuthExpired(false); // <-- hide banner after successful login
      isLoggingOutRef.current = false;

      // ✅ new login window => new client_session_id
      const clientSessionId = generateClientSessionId();
      await AsyncStorage.setItem(CLIENT_SESSION_ID_KEY, clientSessionId);
      axios.defaults.headers.common["X-Client-Session-Id"] = clientSessionId; // optional convenience

      // 🟨 Check if token is already set and different
      const existingToken = await AsyncStorage.getItem("auth_token");
      if (existingToken && existingToken !== token) {
        console.warn("⚠️ Overwriting existing token!", {
          old: existingToken.slice(0, 10),
          new: token.slice(0, 10)
        });
      }

      setAuthToken(token);
      setUser(studentData);
      await AsyncStorage.setItem("auth_token", token);
      await AsyncStorage.setItem("student_data", JSON.stringify(studentData));

      // 2) Configure Axios defaults (keep your current convention)
      axios.defaults.headers.common["Authorization"]  = token;
      axios.defaults.headers.common["X-User-Email"]   = studentData.email;
      console.log("[AuthContext]  axios headers after login():", axios.defaults.headers.common);

      // seed contentDb from studentData if present, before hitting /profile
      if (studentData?.content_db) {
        setContentDb(String(studentData.content_db).toLowerCase());
        axios.defaults.headers.common["X-Content-DB"] = String(studentData.content_db).toLowerCase();
      }

      // 3) Immediately fetch /api/profile to get content_db
      try {
        console.log("[AuthContext]  fetching profile …");
        const resp = await axios.get("profile");
        const db   = resp.data.content_db || "anatomy";
        setContentDb(db);
        axios.defaults.headers.common["X-Content-DB"] = db;
        console.log("[AuthContext]  set contentDb =", db);
        // BDD 12/18/2025 - merge profile flags (e.g., can_view_all_classes) into user
        setUser(prev => ({ ...(prev || {}), ...resp.data }));
        await AsyncStorage.setItem("student_data", JSON.stringify({ ...(studentData || {}), ...resp.data }));
      } catch (err) {
        console.error("Failed to fetch profile:", err);
        // keep whatever contentDb is already set (default or studentData.content_db)
      }
    };


    // 10/3/2025
    const SOFT_KEEP_KEYS = [
      "student_data",             // keep profile (same user)
      "secondary_language_id",    // UI pref
      // add any image/presign caches you maintain, e.g. "image_url_cache"
    ];

    const logout = async (reason = "manual", { hard = false } = {}) => {
      isLoggingOutRef.current = true;
      console.log(`🚪 logout() called — reason: ${reason} hard=${hard}`);

      // reset in-memory auth + banner
      setAuthToken(null);
      setUser(null);
      setAuthExpired(false); // <-- ensure the banner disappears on logout

      // clear stored tokens
      // const removeKeys = ["auth_token", "refresh_token", "previous_access_token"];
      const removeKeys = ["auth_token", "refresh_token", "previous_access_token", CLIENT_SESSION_ID_KEY];

      if (hard) {
        await AsyncStorage.multiRemove([...removeKeys, ...SOFT_KEEP_KEYS]);
      } else {
        await AsyncStorage.multiRemove(removeKeys);
      }

      // prevent stale axios Authorization from leaking into next session
      try {
        if (axios?.defaults?.headers?.common) {
          delete axios.defaults.headers.common["Authorization"];
          delete axios.defaults.headers.common["X-Client-Session-Id"];
        }
      } catch (_) {}

      console.log("✅ Logout complete.");
    };

    // helper to change secondary language
    const updateSecondaryLang = async (lang) => {
      // Collapse "auto"/"none"/"null"/empty sentinels to "en" so the chip never
      // renders "NONE", and persist the normalized value so it can't re-leak on reload.
      const normalized = normalizeSecondaryLang(lang);
      setSelectedSecondaryLang(normalized);
      await AsyncStorage.setItem("secondary_language_id", normalized);
    };

    // Refetch profile from backend (useful after onboarding completes)
    const refetchProfile = async () => {
      try {
        console.log("[AuthContext] refetchProfile() called");
        const resp = await axios.get("profile");
        const db = resp.data.content_db || "anatomy";
        setContentDb(db);
        axios.defaults.headers.common["X-Content-DB"] = db;
        console.log("[AuthContext] refetchProfile set contentDb =", db);

        // Merge profile data into user state
        setUser(prev => ({ ...(prev || {}), ...resp.data }));

        // Update async storage
        const existingData = await AsyncStorage.getItem("student_data");
        const existing = existingData ? JSON.parse(existingData) : {};
        await AsyncStorage.setItem("student_data", JSON.stringify({ ...existing, ...resp.data }));

        return resp.data;
      } catch (err) {
        console.error("[AuthContext] refetchProfile failed:", err);
        throw err;
      }
    };

    const ensureSession = (rid = 0, caller = "manual") => {
      if (!ENABLE_ENSURE) return Promise.resolve({ ok: true, reason: "disabled" });
      if (ENSURE_PROMISE) {
        console.warn(`[ensure][${rid}] join in-flight (caller=${caller})`);
        return ENSURE_PROMISE;
      }

      ENSURE_PROMISE = (async () => {
        try {
          const token    = await AsyncStorage.getItem("auth_token");
          const refresh  = await AsyncStorage.getItem("refresh_token");
          const deviceId = await AsyncStorage.getItem("device_id");
          console.warn(`[ensure][${rid}] start (caller=${caller}) hasToken=${!!token} hasRefresh=${!!refresh} device=${deviceId}`);

          if (!token || !refresh || !deviceId) {
            return { ok: false, code: "missing_creds", message: "Missing token/refresh/device" };
          }

          // NOTE: Only this request uses Bearer; do NOT touch axios.defaults here.
          const resp = await axios.post(
            `${FASTAPI_BASE_URL}/auth/ensure`,
            {},
            { headers: { Authorization: `Bearer ${token}`, "X-Refresh-Token": refresh, "Device-ID": deviceId } }
          );

          if (resp?.status === 200 && resp.data?.access_token && resp.data?.refresh_token) {
            const newAccess  = resp.data.access_token;
            const newRefresh = resp.data.refresh_token;
            console.warn(`[ensure][${rid}] success → rotating tokens (prevAccess[:12]=${(token||"").slice(0,12)}…, newAccess[:12]=${newAccess.slice(0,12)}…)`);

            await AsyncStorage.setItem("auth_token", newAccess);
            await AsyncStorage.setItem("refresh_token", newRefresh);
            setAuthToken(newAccess);
            // this is OLD comment -- DO NOT set axios.defaults.headers.common["Authorization"] here.
            // BDD 9/7/2025 -- 
            // We DO update axios.defaults to the new token (no Bearer) to keep other axios calls working.
            // ✅ Update axios default so axios-based APIs use the fresh token.
            //    Keep your existing convention: no "Bearer" here.
            axios.defaults.headers.common["Authorization"] = newAccess;

            lastRotationAtRef.current = Date.now();
            return { ok: true, reason: "refreshed" };
          }
          return { ok: false, code: "ensure_failed", message: "No tokens returned" };
        } catch (err) {
          const status = err?.response?.status;
          const data   = err?.response?.data || {};
          console.warn(`[ensure][${rid}] error status=${status} body=`, data);
          if (status === 401) {
            return { ok: false, code: data.code || "unauthorized", message: data.message || "Unauthorized" };
          }
          return { ok: false, code: "network_or_server", message: String(err) };
        } finally {
          console.warn(`[ensure][${rid}] done`);
          ENSURE_PROMISE = null;
        }
      })();

      return ENSURE_PROMISE;
    };


    return (
      <AuthContext.Provider
        value={{
          isLoading,
          authToken,
          user,
          contentDb,
          login,
          logout,
          fetchWithAuth,
          selectedSecondaryLang,
          updateSecondaryLang,
          refetchProfile,
          authExpired,
          setAuthExpired,
        }}
      >
        <View style={{ flex: 1, opacity: authExpired ? 0.4 : 1 }}>
          {children}
        </View>

        {/* Full-screen expired-session overlay (now a child component) */}
        <AuthExpiredOverlay />
      </AuthContext.Provider>
    );

};
const AuthExpiredOverlay = () => {
  const { t } = useTranslation();   // safe here (we’re inside the Provider)
  const { logout, authExpired } = useAuth();
  if (!authExpired) return null;

  return (
    <View style={stylesAuth.overlay} pointerEvents="auto" accessibilityRole="alert">
      <View style={stylesAuth.card}>
        <Text style={stylesAuth.title}>{t("Session expired")}</Text>
        <Text style={stylesAuth.subtitle}>
          {t("Session expired. Please sign in again to continue.")}
        </Text>

        <TouchableOpacity onPress={logout} style={stylesAuth.cta} accessibilityRole="button">
          <Text style={stylesAuth.ctaText}>{t("Sign in again")}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const stylesAuth = StyleSheet.create({
  overlay: {
    position: "absolute",
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: "rgba(0,0,0,0.6)",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10000,           // ensure above everything
    elevation: 10000,        // Android
  },
  card: {
    width: "90%",
    maxWidth: 420,
    backgroundColor: "#fff",
    borderRadius: 16,
    paddingVertical: 20,
    paddingHorizontal: 18,
    alignItems: "center",
    shadowColor: "#000",
    shadowOpacity: 0.2,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 6 },
  },
  title: {
    fontSize: 18,
    fontWeight: "700",
    marginBottom: 8,
    color: "#222",
    textAlign: "center",
  },
  subtitle: {
    fontSize: 14,
    color: "#444",
    textAlign: "center",
    marginBottom: 18,
  },
  cta: {
    marginTop: 4,
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 16,
    backgroundColor: "#0077B6",
  },
  ctaText: {
    color: "#fff",
    fontWeight: "700",
    fontSize: 15,
  },
});
